import { html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { CallToAction } from '../call-to-action/call-to-action';
import { EyebrowSlot } from '../../slots/eyebrow-slot';
import { HeadlineSlot } from '../../slots/headline-slot';
import { formattedDateTime } from '../../utilities/format-date';
import { renderSimpleStat } from '../../components/stats/simple-stat';

export type PathwayProps = {
  theme?: '' | 'white' | 'light' | 'dark';
  type?: string;
  showAnimatedPathway?: boolean;
  animationEnabled?: boolean;
  imagePosition?: 'left' | 'right';
  scaled?: boolean;
  imageSource?: string;
  imageAltText?: string;
  showDataCaption?: boolean;
  dataCaption?: string;
  eyebrowText?: string;
  showEyebrow?: boolean;
  headline?: string;
  text?: string;
  showLink?: boolean;
  showSecondaryLinks?: boolean;
  showAllSecondaryLinks?: boolean;
  linkText?: string;
  ariaLabel?: string;
  ctaType?: 'primary' | 'secondary';
  showEvent?: boolean;
  startDate?: string;
  endDate?: string;
  isAllDayEvent?: boolean;
  location?: string;
  showStats?: boolean;
  statOne?: string;
  statOneText?: string;
  statOneSubText?: string;
  statTwo?: string;
  statTwoText?: string;
  statTwoSubText?: string;
};

export const Pathway = ({
  theme,
  type,
  animationEnabled,
  imagePosition,
  scaled,
  imageSource,
  imageAltText,
  showDataCaption,
  dataCaption,
  eyebrowText,
  showEyebrow,
  headline,
  text,
  showLink,
  showSecondaryLinks,
  showAllSecondaryLinks,
  linkText,
  ariaLabel,
  ctaType,
  showEvent,
  startDate,
  endDate,
  isAllDayEvent,
  location,
  showStats,
  statOne,
  statOneText,
  statOneSubText,
  statTwo,
  statTwoText,
  statTwoSubText,
}: PathwayProps) => {
  const formattedStartDate = formattedDateTime(startDate);
  const formattedEndDate = formattedDateTime(endDate);

  const statTheme: 'light' | 'dark' | undefined =
    theme === 'light' || theme === 'dark' ? theme : undefined;

  return html`
    <umd-element-pathway
      data-theme="${theme}"
      type="${type}"
      image-position="${imagePosition}"
      image-scaled="${scaled ? 'true' : 'false'}"
      show-time="${ifDefined(isAllDayEvent ? 'false' : 'true')}"
      data-animation="${ifDefined(animationEnabled ? 'true' : undefined)}"
    >
      <img
        src="${imageSource}"
        alt="${imageAltText}"
        slot="image"
        data-caption=${ifDefined(showDataCaption ? dataCaption : undefined)}
      />
      ${showEyebrow ? EyebrowSlot({ eyebrowText }) : ''}
      ${HeadlineSlot({ headline })}

      <div slot="text">${text}</div>

      ${showLink
        ? CallToAction({
            theme,
            type: ctaType ?? 'primary',
            linkText,
            ariaLabel,
          })
        : ''}
      ${showSecondaryLinks
        ? html`<div slot="actions">
            ${CallToAction({
              theme,
              type: 'secondary',
              linkText: 'Learn more',
              ariaLabel: 'Learn more about Lorem ipsum',
            })}
            ${showAllSecondaryLinks
              ? html` ${CallToAction({
                  theme,
                  type: 'secondary',
                  linkText: 'Volunteer for a campus event',
                  ariaLabel: '',
                })}
                ${CallToAction({
                  theme,
                  type: 'secondary',
                  linkText: 'Serve as an election judge',
                  ariaLabel: '',
                })}
                ${CallToAction({
                  theme,
                  type: 'secondary',
                  linkText: 'Promote campus civic engagement',
                  ariaLabel: '',
                })}
                ${CallToAction({
                  theme,
                  type: 'secondary',
                  linkText: 'Find voter information in your state',
                  ariaLabel: '',
                })}`
              : ''}
          </div>`
        : ''}
      ${showEvent
        ? html` <div slot="start-date-iso">${formattedStartDate}</div>

            <div slot="end-date-iso">${formattedEndDate}</div>

            <div slot="location">${location}</div>`
        : ''}
      ${showStats
        ? html`<div slot="stats">
            ${[
              { stat: statOne, text: statOneText, subText: statOneSubText },
              { stat: statTwo, text: statTwoText, subText: statTwoSubText },
            ].map(({ stat, text, subText }) =>
              renderSimpleStat({
                theme: statTheme,
                stat,
                text,
                subText,
                size: '',
                hasLine: false,
                displayType: '',
              }),
            )}
          </div>`
        : ''}
    </umd-element-pathway>
  `;
};
